import { isNone, usePeriodicCallback } from '@crocodille/common';
import cn from 'classnames';
import Image, { ImageProps } from '../elements/image/image';
import Link, { LinkProps } from 'next/link';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CircleButton from '../elements/circle-button/circle-button';
import ChevronLeftIcon from '../icons/chevron-left-icon';
import ChevronRightIcon from '../icons/chevron-right-icon';
import styles from './horizontal-slider.module.scss';
import { useMedia } from '@hooks/use-media';
import Button from '@components/elements/button/button';
import { useFocusWithin } from '@hooks/use-focus-within';
import { useScrollState } from '@hooks/use-scroll-state';

interface HorizontalSliderElementProps {
  readonly autoAdvanceInterval?: number;
  readonly isDisplayControls?: boolean;
  readonly title?: string;
  readonly isAdditionalPadding?: boolean;
  readonly href?: string;
}

const HorizontalSliderElement: React.FC<
  React.PropsWithChildren<HorizontalSliderElementProps>
> = ({
  autoAdvanceInterval,
  children,
  isDisplayControls,
  title,
  isAdditionalPadding,
  href,
}) => {
  const { t } = useTranslation();
  const listRef = useRef<HTMLUListElement>(null);
  const [visibleSlideIndex, setVisibleSlideIndex] = useState<number>(0);
  const isMobile = useMedia('shipping-page-shipping-section', true);

  const slideCount = React.Children.count(children);

  const prev = useCallback(() => {
    if (!listRef.current) return;

    const slide = listRef.current.querySelector('li');
    if (slide) {
      listRef.current.scrollBy({
        left: -slide.clientWidth,
        behavior: 'smooth',
      });
    }
  }, []);

  const next = useCallback(() => {
    if (!listRef.current) return;

    const slide = listRef.current.querySelector('li');
    if (slide) {
      listRef.current.scrollBy({
        left: slide.clientWidth,
        behavior: 'smooth',
      });
    }
  }, []);

  useEffect(() => {
    if (isNone(listRef.current)) return;

    const firstSlide = listRef.current.querySelector('li');
    listRef.current.scrollTo({
      left: firstSlide?.offsetLeft,
      behavior: 'auto',
    });
  }, []);

  const scrollToSlide = useCallback(
    (index: number) => {
      if (!listRef.current) return;

      const slides = listRef.current.querySelectorAll('li');
      const targetSlide = slides[index];

      if (targetSlide) {
        listRef.current.scrollTo({
          left: targetSlide.offsetLeft,
          behavior: 'smooth',
        });
        setVisibleSlideIndex(index);
      }
    },
    [listRef],
  );

  const controls: React.ReactNode[] = [];

  React.Children.forEach(children, (slide, index) => {
    if (!slide) return;

    controls.push(
      <button
        key={index}
        className={cn(styles.control, {
          [styles.isActive]:
            index === (visibleSlideIndex - 1) % React.Children.count(children),
        })}
        type="button"
        onClick={() => scrollToSlide(index)}
      ></button>,
    );
  });

  const containerRef = useRef<HTMLDivElement>(null);

  const isFocused = useFocusWithin(containerRef);
  const [isHovered, setIsHovered] = useState(false);
  const isScrolling = useScrollState(listRef, 500);

  const autoAdvanceEnabled =
    autoAdvanceInterval != null && !isHovered && !isFocused && !isScrolling;

  usePeriodicCallback(
    (index) => {
      if (index > 0) {
        scrollToSlide((visibleSlideIndex + 1) % slideCount);
      }
    },
    {
      period: autoAdvanceInterval,
      enabled: autoAdvanceEnabled,
    },
  );

  return (
    <>
      {title && (
        <div className={styles.titleContainer}>
          <h2>{title}</h2>
          <div className={styles.titleButtons}>
            <div className={styles.controlBtns}>
              <CircleButton
                onClick={prev}
                hiddenLabel={true}
                label={t('horizontal_slider.prev')}
                basic
              >
                <div className={cn(styles.arrow, styles.left)}></div>
              </CircleButton>
              <CircleButton
                onClick={next}
                hiddenLabel={true}
                label={t('horizontal_slider.next')}
                basic
              >
                <div className={cn(styles.arrow, styles.right)}></div>
              </CircleButton>
              {href && (
                <Button
                  as="a"
                  href={href}
                  round
                  variant="basic"
                  className={styles.allBtn}
                >
                  {t('all_btn')}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      <div
        ref={containerRef}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={cn(styles.container, {
          [styles.additionalPadding]: isAdditionalPadding,
        })}
      >
        {isDisplayControls && !isMobile && (
          <CircleButton
            onClick={prev}
            className={cn(styles.button, styles.prev)}
            hiddenLabel={true}
            inverse={true}
            label={t('horizontal_slider.prev')}
          >
            <ChevronLeftIcon />
          </CircleButton>
        )}
        <ul
          ref={listRef}
          className={cn(styles.list, {
            [styles.singleSlide]: slideCount === 1,
            [styles.additionalPadding]: isAdditionalPadding,
          })}
        >
          {children}
        </ul>
        {isDisplayControls && !isMobile && (
          <CircleButton
            onClick={next}
            className={cn(styles.button, styles.next)}
            hiddenLabel={true}
            inverse={true}
            label={t('horizontal_slider.next')}
          >
            <ChevronRightIcon />
          </CircleButton>
        )}
        {isDisplayControls && <div className={styles.controls}>{controls}</div>}
      </div>
    </>
  );
};

export type SlideProps = Pick<ImageProps, 'src'> & {
  readonly href: LinkProps['href'] | null;
  readonly label: string;
  readonly className?: string;
  readonly size?: 'small' | 'medium' | 'large';
  readonly isSmallImg?: boolean;
  readonly asAnchor?: boolean;
};

const Slide: React.FC<React.PropsWithChildren<SlideProps>> = ({
  children,
  href,
  src,
  label,
  size = 'medium',
  isSmallImg,
  asAnchor,
}) => {
  const image = (
    <div
      className={cn(styles.imageContainer, {
        [styles.smallImg]: isSmallImg,
      })}
    >
      <Image
        src={src}
        alt={label}
        layout="fill"
        objectFit="cover"
        priority={false}
        loading={'lazy'}
        className={styles.img}
      />
    </div>
  );

  return (
    <li className={cn(styles.slide, styles[size])}>
      {href ? (
        asAnchor ? (
          <a href={href?.toString()} className={cn(styles.slideLink)}>
            {image}
          </a>
        ) : (
          <Link href={href} className={cn(styles.slideLink)} prefetch={false}>
            {image}
            {children}
          </Link>
        )
      ) : (
        <div className={cn(styles.slideLink)}>
          {image}
          {children}
        </div>
      )}
    </li>
  );
};

const HorizontalSlider = Object.assign(HorizontalSliderElement, { Slide });

export default HorizontalSlider;
